import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next, view } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import settings from '../settings';
import createSubmitTraitsMediaAppearanceHandler from '../helpers/create-submit-traits-media-appearance-handler';
import controlIds from '../constants/control-ids';

const { Divider, Box, Paragraph } = designSystem;
const {
  Context: ElementSettingsContext,
  components: { ToggleControl, SliderControl, ToggleableSectionControl },
  constants: { stages },
  helpers: { createBatch, generateInitialState },
  hooks: { useMountChunk },
} = elementSettings;

const DESKTOP_ONLY_CONTROL_IDS = [controlIds.BORDER, controlIds.SHADOW, controlIds.CORNERS];

const createControlVisibilityChecker = (deviceMode) => (controlId) =>
  ![view.deviceModes.TABLET, view.deviceModes.MOBILE].includes(deviceMode?.active) ||
  !DESKTOP_ONLY_CONTROL_IDS.includes(controlId);

const AllMediaAppearanceSettingsContainer = ({ tabId, elementId, settingId }) => {
  const chunkId = tabId;
  const { callbacks, deviceMode, onUpdateState } = useContext(ElementSettingsContext);

  const controlSettings = useMemo(
    () => settings.getAllMediaAppearanceSettings(elementId, settingId),
    [elementId, settingId]
  );

  const handleUpdateState = useCallback(
    (batch) => {
      onUpdateState(batch, { chunkId });
    },
    [chunkId, onUpdateState]
  );

  const handleChange = useCallback(
    ({ controlId, elementIds, targets, value, skipTracking = false }) => {
      const batch = createBatch({ elementIds, targets, value });

      handleUpdateState(batch);
      callbacks.onControlClick({ controlId, settingId, skipTracking });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleToggleChange = useCallback(
    ({ controlId, elementIds, targets, value }) => {
      const batch = createBatch({ elementIds, targets, value });

      handleUpdateState(batch);
      callbacks.onToggleClick({ controlId, settingId, isActive: value });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleToggleableSectionChange = useCallback(
    ({ batch, controlId, value }) => {
      handleUpdateState(batch);
      callbacks.onToggleClick({ controlId, settingId, isActive: value });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const traitsHandler = useMemo(
    () =>
      createSubmitTraitsMediaAppearanceHandler({
        prefix: settingId,
      }),
    [settingId]
  );

  const { isChunkMounted } = useMountChunk(chunkId, {
    tabId,
    initialState: generateInitialState(Object.values(controlSettings)),
    stageHandlers: [
      {
        stage: stages.SUBMIT_TRAITS_GENERATION,
        handler: traitsHandler,
      },
    ],
    withUnmount: true,
  });

  if (!isChunkMounted) return null;

  const isControlVisible = createControlVisibilityChecker(deviceMode);

  if (![controlIds.BORDER, controlIds.SHADOW, controlIds.CORNERS].some(isControlVisible)) {
    return (
      <Box padding="spacing-m">
        <Paragraph appearance="low-emphasis">
          {i18next.t('None of the options are currently supported in this mode.')}
        </Paragraph>
      </Box>
    );
  }

  return (
    <>
      {isControlVisible(controlIds.BORDER) && (
        <>
          <ToggleControl {...controlSettings[controlIds.BORDER]} onChange={handleToggleChange} />
          <Divider appearance="light-grey" />
        </>
      )}
      {isControlVisible(controlIds.SHADOW) && (
        <>
          <ToggleControl {...controlSettings[controlIds.SHADOW]} onChange={handleToggleChange} />
          <Divider appearance="light-grey" />
        </>
      )}
      {isControlVisible(controlIds.CORNERS) && (
        <ToggleableSectionControl
          {...controlSettings[controlIds.CORNERS_SECTION]}
          onChange={handleToggleableSectionChange}
        >
          <SliderControl {...controlSettings[controlIds.CORNERS]} onChange={handleChange} />
        </ToggleableSectionControl>
      )}
    </>
  );
};

AllMediaAppearanceSettingsContainer.propTypes = {
  tabId: PropTypes.string.isRequired,
  elementId: PropTypes.string.isRequired,
  settingId: PropTypes.string.isRequired,
};

export default AllMediaAppearanceSettingsContainer;
